import { useEffect, useMemo, useState } from 'react'
import Button from '@/shared/Buttons/Button'
import DropdownInput from '@/shared/Inputs/DropdownInput'
import SearchInput from '@/shared/Inputs/SearchInput'
import PlusIcon from '@/assets/icons/plus.svg?react'
import UploadIcon from '@/assets/icons/upload.svg?react'
import CmsEntityForm from './CmsEntityForm'
import CmsEntityTable from './CmsEntityTable'
import GalleryImagesBatchUpload from './GalleryImagesBatchUpload'
import useCmsResource from '../hooks/useCmsResource'
import { CmsEntity, CmsLookupContext, CmsResourceConfig, IGalleryImage } from '../cms.types'
import css from '../cms.module.css'

interface IProps<T extends CmsEntity & Record<string, any>> {
  resource: CmsResourceConfig<T>
  context: CmsLookupContext
  assetsLoading: boolean
  onAssetsRefresh: () => Promise<void>
  onLookupsRefresh: () => void
}

const CmsResourceWorkspace = <T extends CmsEntity & Record<string, any>>(props: IProps<T>) => {
  const isGalleryImages = props.resource.key === 'galleryImages'
  const [batchUploadOpen, setBatchUploadOpen] = useState(false)
  const [galleryCategoryFilter, setGalleryCategoryFilter] = useState<number | ''>('')

  const listParams = useMemo(
    () => (isGalleryImages && galleryCategoryFilter ? { categoryId: galleryCategoryFilter } : undefined),
    [isGalleryImages, galleryCategoryFilter]
  )

  const galleryCategoryOptions = useMemo(
    () => [
      { value: '', title: 'Все категории' },
      ...props.context.galleryCategories.map((category) => ({ value: category.id, title: category.title })),
    ],
    [props.context.galleryCategories]
  )

  useEffect(() => {
    if (!galleryCategoryFilter) return

    const categoryExists = props.context.galleryCategories.some((category) => category.id === galleryCategoryFilter)
    if (!categoryExists) setGalleryCategoryFilter('')
  }, [galleryCategoryFilter, props.context.galleryCategories])

  const ctrl = useCmsResource(props.resource, props.context, props.onLookupsRefresh, listParams)

  const selectGalleryCategoryFilter = (value: string | number) => {
    setGalleryCategoryFilter(value === '' ? '' : Number(value))
  }

  return (
    <>
      <div className={css.toolbar}>
        <div>
          <h1 className="text-heading-4">{props.resource.title}</h1>
          <p className="text-nm text-description">
            {props.resource.getSummary ? props.resource.getSummary(ctrl.items) : `${ctrl.items.length} записей`}
          </p>
        </div>
        <div className="inline-flex-gap gap8 center">
          <div style={{ width: 300 }}>
            <SearchInput
              value={ctrl.search}
              name="search"
              onChange={(value) => ctrl.setSearch(value)}
              placeHolder="Поиск"
            />
          </div>
          {isGalleryImages && (
            <div style={{ width: 260 }}>
              <DropdownInput
                options={galleryCategoryOptions}
                selectedOption={galleryCategoryFilter}
                name="categoryId"
                title="Все категории"
                onSelect={selectGalleryCategoryFilter}
              />
            </div>
          )}
          {isGalleryImages && (
            <Button
              size="default"
              type="default"
              text="Загрузить пачкой"
              icon={<UploadIcon />}
              onClick={() => setBatchUploadOpen(true)}
            />
          )}
          <Button size="default" type="primary" text="Создать" icon={<PlusIcon />} onClick={ctrl.openCreate} />
        </div>
      </div>

      <div className={css.tablePanel}>
        <CmsEntityTable
          resource={props.resource}
          items={ctrl.filteredItems}
          loading={ctrl.loading}
          context={props.context}
          onEdit={ctrl.openEdit}
          onDelete={ctrl.remove}
        />
      </div>

      <CmsEntityForm
        resource={props.resource}
        editor={ctrl.editor}
        context={props.context}
        assetsLoading={props.assetsLoading}
        saving={ctrl.saving}
        onChange={ctrl.changeField}
        onClose={ctrl.closeEditor}
        onSave={ctrl.save}
        onAssetsRefresh={props.onAssetsRefresh}
      />

      {isGalleryImages && (
        <GalleryImagesBatchUpload
          open={batchUploadOpen}
          categories={props.context.galleryCategories}
          images={ctrl.items as unknown as IGalleryImage[]}
          initialCategoryId={galleryCategoryFilter || undefined}
          onClose={() => setBatchUploadOpen(false)}
          onUploaded={async () => {
            await ctrl.reload()
            props.onLookupsRefresh()
          }}
          onAssetsRefresh={props.onAssetsRefresh}
        />
      )}
    </>
  )
}

export default CmsResourceWorkspace
